import React, { useMemo } from 'react';
import type { ReactNode } from 'react';
import moment from 'moment-timezone';
import type {
  Components,
  DateLocalizer,
  ToolbarProps,
} from 'react-big-calendar';
import type { CalendarComponentEvent } from '@axiom/validation';

import { calendarLocalizer } from './calendar-localizer';
import { CalendarFormatUtil } from './calendar-format-util';
import { CalendarEvent } from './CalendarEvent';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarTimeSlot } from './CalendarTimeSlot';
import { CalendarDayColumn } from './CalendarDayColumn';

type UseCalendarConfigurationParams = {
  timezone?: string;
  displayDate: Date;
  scrollToTime?: Date;
  toolbarLeftSlot?: ReactNode;
};

type UseCalendarConfigurationReturn = {
  appLocalizer: DateLocalizer;
  defaultDate: Date;
  toTime: Date;
  formats: {
    dayRangeHeaderFormat: (
      range: { start: Date; end: Date },
      culture?: string,
      localizer?: DateLocalizer
    ) => string;
    dayFormat: (
      date: Date,
      culture?: string,
      localizer?: DateLocalizer
    ) => string;
    eventTimeRangeFormat: (
      range: { start: Date; end: Date },
      culture?: string,
      localizer?: DateLocalizer
    ) => string;
  };
  components: Components<CalendarComponentEvent>;
};

export const useCalendarConfiguration = ({
  timezone,
  displayDate,
  scrollToTime,
  toolbarLeftSlot,
}: UseCalendarConfigurationParams): UseCalendarConfigurationReturn => {
  const { appLocalizer, defaultDate, toTime } = useMemo(() => {
    const nowMoment = timezone ? moment.tz(timezone) : moment();

    // react-big-calendar's TimeGrid reads scrollToTime via the moment
    // localizer in componentDidMount, before RawCalendar's layout effect
    // sets moment.tz default to `timezone`. So time-of-day comes out in
    // browser-local. Build a Date whose browser-local h:mm matches the
    // h:mm of `scrollToTime` in `timezone`, mirroring how `nowMoment` is
    // made timezone-aware above.
    const computedToTime = (() => {
      if (!scrollToTime) return nowMoment.subtract(1, 'hour').toDate();
      if (!timezone) return scrollToTime;
      const target = moment.tz(scrollToTime, timezone);
      const adjusted = new Date();
      adjusted.setHours(target.hour(), target.minute(), 0, 0);
      return adjusted;
    })();

    return {
      defaultDate: displayDate,
      // Custom localizer patches react-big-calendar's browser-relative
      // timezone math to honour the configured `timezone`. See
      // calendar-localizer.
      appLocalizer: calendarLocalizer(timezone),
      toTime: computedToTime,
    };
  }, [timezone, displayDate, scrollToTime]);

  // https://github.com/jquense/react-big-calendar/blob/master/stories/demos/exampleCode/rendering.js
  const { formats, components } = useMemo(
    () => ({
      formats: {
        dayRangeHeaderFormat: CalendarFormatUtil.formatDayRangeHeader,
        dayFormat: CalendarFormatUtil.formatDay,
        eventTimeRangeFormat: CalendarFormatUtil.formatEventTimeRange,
        // Drag-to-select overlay label. Without this, RBC falls back to
        // the moment localizer's 'LT' format ('h:mm A' in en-US — capital
        // AM/PM), which doesn't match how the in-event labels render
        // post-drop. Sharing the same formatter eliminates the visual
        // jump between drag and dropped states.
        selectRangeFormat: CalendarFormatUtil.formatEventTimeRange,
      },
      /**
       * react-big-calendar@0.38.0 type definitions are incorrect:
       * - Types define timeSlotWrapper/dayColumnWrapper as ComponentType<{}>
       * - Library actually passes props and refs to these components at runtime
       * - TypeScript requires 'as unknown' for incompatible type assertions
       * - This is a library bug, not a flaw in our component implementations
       */
      components: {
        toolbar: (props: ToolbarProps<CalendarComponentEvent>) => (
          <CalendarToolbar
            date={props.date}
            label={props.label}
            localizer={props.localizer}
            onNavigate={props.onNavigate}
            onView={props.onView}
            view={props.view}
            views={props.views}
            leftSlot={toolbarLeftSlot}
          />
        ),
        event: CalendarEvent,
        timeSlotWrapper: CalendarTimeSlot,
        dayColumnWrapper: CalendarDayColumn,
      } as unknown as Components<CalendarComponentEvent>,
    }),
    [toolbarLeftSlot]
  );

  return {
    appLocalizer,
    defaultDate,
    toTime,
    formats,
    components,
  };
};
